<!-- A generative AI model wrote or edited portions of this file with the supervision of a human developer and careful human review. -->
<script>
  import { LayerCake, Svg, Html } from "layercake";
  import { scaleBand } from "d3-scale";
  import { max } from "d3-array";
  import { BasicDropdown, AxisX, AxisY, Tooltip } from "@urbaninstitute/dataviz-components";
  import { urbanColors } from "@urbaninstitute/dataviz-components/utils";
  import GroupedColumn from "$lib/components/Chart/GroupedColumn.svelte";
  import GroupedBar from "$lib/components/Chart/GroupedBar.svelte";
  import HtmlLabels from "$lib/components/Chart/HtmlLabels.svelte";
  import ReferenceLine from "$lib/components/Chart/ReferenceLine.svelte";
  import { getStateName } from "$utils/states.js";
  import { FORMATTERS, FORMAT, LEGEND_FORMATTERS } from "$utils/formatters.js";
  import { TRANSIT_MODES, TRANSIT_MODES_DROPDOWN } from "$utils/consts.js";
  import windowWidth from "$lib/stores/windowWidth.js";

  const { parsedData, currentState } = $props();

  const GROUPS = ["quarter_mile", "half_mile"];
  const COLORS = [urbanColors.blue, urbanColors.blue_shade_darker];
  const LABELS = ["Quarter mile", "Half mile"];
  const fmt = FORMATTERS[FORMAT.PERCENT_TENTHS];
  const axisFmt = LEGEND_FORMATTERS[FORMAT.PERCENT_TENTHS];

  let mode = $state("bus_all_15");
  let hoveredData = $state(null);
  let activeGroup = $state(undefined);
  let activeIndex = $state(undefined);
  let activeBarEl = $state(null);

  const urbanData = $derived.by(() => {
    if (!parsedData || !currentState) return [];
    const allDetail = parsedData.getDetailDataForId(currentState);
    const rows = allDetail.filter((r) => r.date === "2022-01-01");
    const qCol = `key_shr_hsg_${mode}_quarter`;
    const hCol = `key_shr_hsg_${mode}_half`;
    return rows
      .map((r) => ({
        name: r.name_e,
        quarter_mile: r[qCol],
        half_mile: r[hCol],
        pop: r.pop_20_24
      }))
      .sort((a, b) => (b.pop ?? 0) - (a.pop ?? 0))
      .slice(0, 5);
  });

  const stateBaseline = $derived.by(() => {
    if (!parsedData || !currentState) return null;
    const row = parsedData.getRow(currentState, "2022-01-01");
    return {
      urb_avg_quarter: row[`key_shr_hsg_${mode}_quarter_urb_average`],
      urb_avg_half: row[`key_shr_hsg_${mode}_half_urb_average`]
    };
  });

  const yMax = $derived.by(() => {
    if (!parsedData) return 1;
    const allDetail = parsedData.getDetailData() || [];
    const rows = allDetail.filter((r) => r.date === "2022-01-01");
    const qCol = `key_shr_hsg_${mode}_quarter`;
    const hCol = `key_shr_hsg_${mode}_half`;
    const vals = rows.flatMap((r) => [r[qCol], r[hCol]].filter((v) => v != null));
    return vals.length ? Math.max(max(vals) * 1.2, 0.01) : 1;
  });

  const stateName = $derived(currentState ? getStateName(currentState) : "");
  const isSmall = $derived($windowWidth < 768);
  const legendItems = $derived.by(() => {
    if (!isSmall && stateBaseline) {
      return [
        { label: "Quarter mile", color: COLORS[0], type: "fill" },
        {
          label: `Quarter-mile average across state’s urban areas`,
          color: COLORS[0],
          type: "line"
        },
        { label: "Half mile", color: COLORS[1], type: "fill" },
        { label: `Half-mile average across state’s urban areas`, color: COLORS[1], type: "line" }
      ];
    }

    return [
      { label: "Quarter mile", color: COLORS[0], type: "fill" },
      { label: "Half mile", color: COLORS[1], type: "fill" }
    ];
  });

  function handleHover(e, d, group, gi, di) {
    hoveredData = { name: d.name, group: LABELS[gi], value: d[group] };
    activeGroup = gi;
    activeIndex = di;
  }

  function handleLeave() {
    hoveredData = null;
    activeGroup = undefined;
    activeIndex = undefined;
  }
</script>

{#if currentState}
  <h5 class="section-title">In {stateName}, how does access to transit vary by urban area?</h5>
  <div class="dropdown-row">
    <BasicDropdown
      data={TRANSIT_MODES_DROPDOWN}
      bind:value={mode}
      inlineLabel="Select transit mode"
      id="section-b-mode"
      placeholder={null}
    />
  </div>
  <p class="chart-label">
    Share of housing units within a quarter mile or half mile of {TRANSIT_MODES?.[
      mode
    ]?.toLowerCase()} in the most populous urban areas located at least partly in the state
  </p>
  <div class="reference-legend" aria-label="Chart legend">
    {#each legendItems as item}
      <div class="reference-legend-item">
        {#if item.type === "fill"}
          <span class="reference-legend-swatch" style={`--legend-color: ${item.color};`}></span>
        {:else}
          <span class="reference-legend-line" style={`--legend-color: ${item.color};`}></span>
        {/if}
        <span>{item.label}</span>
      </div>
    {/each}
  </div>
  {#if urbanData.length > 0}
    <div class="chart-container" class:chart-container-bar={isSmall}>
      {#if isSmall}
        <LayerCake
          padding={{ top: 10, right: 40, bottom: 30, left: 80 }}
          y="name"
          x="quarter_mile"
          yScale={scaleBand().paddingInner(0.2).paddingOuter(0.15)}
          xDomain={[0, yMax]}
          data={urbanData}
        >
          <Svg>
            <AxisX formatTick={axisFmt} ticks={3} gridlines />
            <GroupedBar
              groups={GROUPS}
              colors={COLORS}
              separatorEffect="stroke"
              strokeColor={urbanColors.gray_shade_lightest}
              liftOnHover={true}
              {activeGroup}
              {activeIndex}
              bind:activeEl={activeBarEl}
              onhover={handleHover}
              onleave={handleLeave}
            />
          </Svg>
          <Html pointerEvents={false}>
            <HtmlLabels axis="y" />
          </Html>
        </LayerCake>
      {:else}
        <LayerCake
          padding={{ top: 10, right: 60, bottom: 50, left: 50 }}
          x="name"
          y="quarter_mile"
          xScale={scaleBand().paddingInner(0.2)}
          yDomain={[0, yMax]}
          data={urbanData}
        >
          <Svg>
            <AxisY formatTick={axisFmt} ticks={4} gridlines />
            <GroupedColumn
              groups={GROUPS}
              colors={COLORS}
              separatorEffect="stroke"
              strokeColor={urbanColors.gray_shade_lightest}
              liftOnHover={true}
              {activeGroup}
              {activeIndex}
              bind:activeEl={activeBarEl}
              onhover={handleHover}
              onleave={handleLeave}
            />
            {#if stateBaseline}
              <ReferenceLine
                value={stateBaseline.urb_avg_quarter}
                color={COLORS[0]}
                outlineColor={urbanColors.gray_shade_lightest}
              />
              <ReferenceLine
                value={stateBaseline.urb_avg_half}
                color={COLORS[1]}
                outlineColor={urbanColors.gray_shade_lightest}
              />
            {/if}
          </Svg>
          <Html pointerEvents={false}>
            <HtmlLabels axis="x" />
          </Html>
        </LayerCake>
      {/if}
      {#if hoveredData && activeBarEl}
        <Tooltip el={activeBarEl}>
          <h5>{hoveredData.name}</h5>
          <p>
            {fmt(hoveredData.value).replace("%", " percent")} of housing units are within a {hoveredData?.group?.toLowerCase()}.
          </p>
        </Tooltip>
      {/if}
    </div>
  {:else}
    <p class="placeholder-text">No urban area data available for this selection.</p>
  {/if}
{:else}
  <p class="placeholder-text">Select a state to see transit access data by urban area.</p>
{/if}

<style>
  .section-title {
    margin-block-start: 0;
  }
  .dropdown-row {
    margin-bottom: var(--spacing-4);
  }
  .placeholder-text {
    color: var(--color-gray-shade-dark);
  }
  .reference-legend {
    display: flex;
    flex-wrap: wrap;
    gap: var(--spacing-2) var(--spacing-6);
    margin-bottom: var(--spacing-4);
  }
  .reference-legend-item {
    align-items: center;
    display: flex;
    font-size: var(--font-size-small);
    gap: var(--spacing-2);
  }
  .reference-legend-swatch {
    background: var(--legend-color);
    border-radius: 2px;
    display: inline-block;
    height: 0.75rem;
    min-width: 0.75rem;
    width: 0.75rem;
  }
  .reference-legend-line {
    display: inline-block;
    height: 8px;
    min-width: 1.5rem;
    position: relative;
    width: 1.5rem;
  }
  .reference-legend-line::before {
    content: "";
    left: 0;
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
  }
  .reference-legend-line::before {
    border-top: 2px dashed var(--legend-color);
  }
  .chart-container {
    width: 100%;
    height: 350px;
    position: relative;
  }
  .chart-container-bar {
    height: 350px;
  }
</style>
